import React, { useState, useEffect } from "react";
import { Box, Button, Typography, TextField, Avatar, IconButton, Modal } from "@mui/material";
import axiosInstance from '../axiosConfig';
import EditIcon from "@mui/icons-material/Edit";
import API_URL from "../config";

const FRAME_WIDTH = 1100; // Breite des nutzbaren SVG-Rahmens (1200px - 100px Seitenabstand)
const MAX_LINES = 16;

const measureTextWidth = (text, font) => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  context.font = font;

  // Skalierungsfaktor: Die SVG-ViewBox ist größer, daher muss dies berücksichtigt werden.
  return context.measureText(text).width;
};

const formatText = (text, maxWidth, font) => {
  if (text.trim() === "") {
    return [""]; // Leere Zeile explizit zurückgeben
  }

  const words = text.split(" ");
  const lines = [];
  let currentLine = "";

  words.forEach((word) => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const lineWidth = measureTextWidth(testLine, font);

    if (lineWidth <= maxWidth) {
      currentLine = testLine; // Zeile bleibt gleich
    } else {
      lines.push(currentLine); // Zeile abschließen
      currentLine = word; // Neues Wort starten
    }
  });

  if (currentLine) {
    lines.push(currentLine); // Letzte Zeile hinzufügen
  }

  return lines;
};


const SchoolProfileHello = ({ onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [schoolProfile, setSchoolProfile] = useState(null);
  const [text, setText] = useState("");
  const [lines, setLines] = useState([]);
  const [error, setError] = useState(false);
  const [showLogo, setShowLogo] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [imageTimestamp, setImageTimestamp] = useState(Date.now());
  const [isPreviewAvailable, setisPreviewAvailable] = useState(false);

  useEffect(() => {
    // Daten aus der Datenbank laden
    const token = localStorage.getItem("authToken");
    axiosInstance
      .get('/profile/school-profile', {
        headers: {
          Authorization: `${token}`,
        },
      })
      .then((response) => {
        if (response.data.profile) {
          setSchoolProfile(response.data.profile);
          const initialText = response.data.profile.helloMessage || "";
          const initialShowLogo = response.data.profile.schoolHelloLogo ?? true; // Standardwert true
          updateTextAndLines(initialText);
          setShowLogo(initialShowLogo);
        }
      })
      .catch((error) => {
        console.error("Fehler beim Abrufen des Schulprofils:", error);
      });
  }, []);

  useEffect(() => {
    if (schoolProfile?.schoolHelloMessage) {
      fetch(`${API_URL}/uploads/school-profiles/${schoolProfile._id}/components/png/Grußwort.png`)
        .then((response) => {
          if (response.ok) {
            setisPreviewAvailable(true);
          } else {
            setisPreviewAvailable(false);
          }
        })
        .catch((error) => {
          console.error('Fehler beim Überprüfen des Grußwortpfades:', error);
          setisPreviewAvailable(false);
        });
    }
  }, [schoolProfile?.schoolHelloMessage]);

  const handleChange = (event, source) => {
    onUpdate({ type: "editStart" });
    if (source === "textField") {
      handleTextChange(event);
    } else if (source === "checkbox") {
      setShowLogo(event.target.checked);
    }
  };  

  const handleTextChange = (event) => {
    const rawText = event.target.value;
    const font = "44px Arial"; // SVG-Schriftgröße
    const manualLines = rawText.split("\n");
  
    let formattedLines = [];
    manualLines.forEach((line) => {
      const wrappedLines = formatText(line || " ", FRAME_WIDTH, font); // Leere Zeilen auch verarbeiten
      formattedLines = formattedLines.concat(wrappedLines);
    });
  
    // Aktualisiere den Text und überprüfe, ob die Zeilenanzahl überschritten wird
    setText(rawText);
    setLines(formattedLines);
    setError(formattedLines.length > MAX_LINES); // Fehler, wenn mehr als 16 Zeilen
  };

  const calculateFormattedLines = (rawText) => {
    const font = "44px Arial"; // SVG-Schriftgröße
    const manualLines = rawText.split("\n");
  
    let formattedLines = [];
    manualLines.forEach((line) => {
      const wrappedLines = formatText(line || " ", FRAME_WIDTH, font); // Leere Zeilen auch verarbeiten
      formattedLines = formattedLines.concat(wrappedLines);
    });
  
    return formattedLines;
  };
  
  const updateTextAndLines = (rawText) => {
    const formattedLines = calculateFormattedLines(rawText);
    setText(rawText);
    setLines(formattedLines);
    setError(formattedLines.length > MAX_LINES); // Fehler, wenn mehr als 16 Zeilen
  };
  

  const handleSave = () => {
    if (error) {
      alert("Der Text überschreitet die maximale Zeilenanzahl. Bitte kürzen.");
      return;
    }
  
    const token = localStorage.getItem("authToken");
    const savedText = lines.join("\n"); // Zeilenumbrüche hinzufügen
  
    axiosInstance
      .post(
        '/profile/school-hello',
        {
          helloMessage: savedText,
          schoolHelloLogo: showLogo, // Checkbox-Wert senden
        },
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      )
      .then((response) => {
        setSchoolProfile(response.data.profile);
        // Aktualisiere den Zeitstempel, um das Nachladen des Bildes zu erzwingen
        setImageTimestamp(Date.now());
        onUpdate({ type: "saveSuccess", profile: response.data.profile } );
        setIsEditing(false);
      })
      .catch((error) => {
        console.error("Fehler beim Speichern des Schulprofils:", error);
        onUpdate({ type: "saveError" });
      });
  };

  return (
    <Box
      sx={{
        position: "relative",
        padding: "1rem",
      }}
    >
      <Typography variant="h5">Grußwort</Typography>

      {/* Editier-Button */}
      {!isEditing && (
        <Button
          variant="contained"
          size="small"
          color='primary'
          sx={{
            backgroundColor: 'primary.light',
            color: '#fff',
            position: 'absolute',
            top: '1rem',
            right: '2rem',
            border: 'none',
            '&:hover': {backgroundColor: 'primary.dark', color: '#fff'} // Etwas dunkler beim Hover
          }}
          onClick={() => {
            setIsEditing(true);
            const initialText = schoolProfile?.schoolHelloMessage || "";
            updateTextAndLines(initialText); // Text und Zeilen aktualisieren
          }}
        >
          Bearbeiten
        </Button>
      )}

      {!isEditing ? (
      <Box sx={{ marginBottom: "1rem" }}>
        <Box sx={{ marginBottom: "1rem" }}>
          <Typography color="black" variant="subtitle1" sx={{ fontWeight: "bold" }}>
            Vorschau Grußwort
          </Typography>
          {schoolProfile?.schoolHelloMessage && isPreviewAvailable ? (
            <Box
              sx={{
                cursor: "pointer",
                display: "inline-block",
                marginBottom: "1rem",
                border: "1px solid #ccc",
                borderRadius: "8px",
                overflow: "hidden",
              }}
              onClick={() => setShowPopup(true)}
            >
              <img
                src={`${API_URL}/uploads/school-profiles/${schoolProfile._id}/components/png/Grußwort.png?timestamp=${imageTimestamp}`}
                alt="Grußwort Vorschau"
                style={{ width: "100%", maxWidth: "300px" }}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Füllen Sie die Formular-Daten aus, um das individuelle Deckblatt zu generieren.
            </Typography>
          )}
          <Box sx={{ marginBottom: '1rem' }}>
            <Typography color="black" variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Schullogo auf Seite integrieren?
            </Typography>
            <Typography style={{ whiteSpace: "pre-line" }} sx={{ color: schoolProfile?.schoolHelloMessage ? "inherit" : "gray" }}>
              {schoolProfile?.schoolHelloLogo ? "Ja" : "Nein"}
          </Typography>
          </Box>
          <Box sx={{ marginBottom: '1rem' }}>
            <Typography color="black" variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Schullogo
            </Typography>
            <Avatar alt="Schullogo" src={schoolProfile?.logoUrl} sx={{ width: 100, height: 100 }} />
          </Box>
        </Box>
          <Typography color="black" variant="subtitle1" sx={{ fontWeight: "bold" }}>
            Grußwort
          </Typography>
          <Typography style={{ whiteSpace: "pre-line" }} sx={{ color: schoolProfile?.schoolHelloMessage ? "inherit" : "gray" }}>
            {schoolProfile?.schoolHelloMessage || "Nicht ausgefüllt"}
          </Typography>
        </Box>
      ) : (
        <Box>
          {schoolProfile?.logoUrl ? (
            <Box>
              <Box sx={{ marginBottom: '1rem' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Schullogo
                </Typography>
                <Avatar alt="Schullogo" src={schoolProfile?.logoUrl} sx={{ width: 100, height: 100, marginBottom: '1rem' }} />
              </Box>
              <Box style={{ display: 'flex'}}>
                <Typography variant="subtitle1" sx={{ marginBottom: "0.5rem" }}>
                  Möchten Sie das Schullogo in die Grußwort-Seite integrieren?
                </Typography>
                <input
                  style={{ marginBottom: '11px'}}
                  type="checkbox"
                  checked={showLogo}
                  onChange={(e) => handleChange(e, "checkbox")}
                />
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="error" sx={{ marginBottom: "1rem" }}>
              Sie haben kein Schullogo in Ihren Schulprofildaten hinterlegt, daher wird kein Schullogo auf die Seite integriert.
            </Typography>
          )}
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            Ihr Text
          </Typography>
          <TextField
            multiline
            fullWidth
            value={text}
            onChange={(e) => handleChange(e, "textField")}
            rows={16} // Zeigt immer 16 Zeilen an
            inputProps={{ style: { fontFamily: "Arial", fontSize: "16px", lineHeight: "20px" } }}
            sx={{
              marginBottom: "0.5rem",
              border: error ? "1px solid red" : "none",
            }}
          />
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color={error ? "error" : "textSecondary"}>
              {error
                ? "Die maximale Zeilenanzahl wurde überschritten!"
                : `Verbleibende Zeilen: ${Math.max(0, 16 - lines.length)}`}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Maximale Zeilen: 16
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSave}
            sx={{ mt: 2, bgcolor: 'primary.light', '&:hover': { color: '#fff' }  }}
            disabled={error}
          >
            Speichern
          </Button>
        </Box>
      )}
      {showPopup && (
      <Modal
        open={showPopup}
        onClose={() => setShowPopup(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: "80%",
            maxWidth: "600px",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: 24,
            padding: "1.5rem",
            position: "relative",
            textAlign: "center",
          }}
        >
          <IconButton
            sx={{
              position: "absolute",
              top: "0.5rem",
              right: "0.5rem",
            }}
            onClick={() => setShowPopup(false)}
          >
            X
          </IconButton>
          <img
            src={`${API_URL}/uploads/school-profiles/${schoolProfile._id}/components/png/Grußwort.png?timestamp=${imageTimestamp}`}
            alt="Grußwort"
            style={{
              width: "100%",
              borderRadius: "8px",
            }}
          />
        </Box>
      </Modal>    
    )}
    </Box>
  );
};

export default SchoolProfileHello;
